/**
 * The colour-blind aid's figure material (spec 08 §4): an angry monster carries a pattern as well as
 * its colour. It is the ordinary toon material with diagonal bands darkened across every surface whose
 * vertex colour lies on the `angry` ramp - the body, the boss's robe - and nothing else (eyes, feet and
 * keys keep their colours, so the figure stays itself). The bands are in each part's own space, so they
 * ride on the figure as it moves instead of swimming over it, and they are wide enough to read at the
 * game's size (about four across a monster).
 */

import type { Color, MeshToonMaterial } from 'three';
import { createToonMaterial, type ToonOptions } from './toon';

/** Bands per world unit (tile) along the diagonal, and how dark the dark ones are. */
const STRIPE_FREQ = 3.2;
const STRIPE_DARK = 0.42;

const STRIPE_VERT_DECL = /* glsl */ `
varying vec3 vStripePos;
`;

const STRIPE_FRAG_DECL = /* glsl */ `
varying vec3 vStripePos;
uniform vec3 uStripeRamp[4];
uniform float uStripeFreq;
uniform float uStripeDark;
`;

// Distance from the vertex colour to the ramp's polyline: blended ramp steps count as the ramp too.
const STRIPE_APPLY = /* glsl */ `
  {
    float onRamp = 1.0;
    for (int i = 0; i < 3; i++) {
      vec3 a = uStripeRamp[i];
      vec3 ab = uStripeRamp[i + 1] - a;
      float t = clamp(dot(vColor.rgb - a, ab) / max(dot(ab, ab), 1e-6), 0.0, 1.0);
      onRamp = min(onRamp, distance(vColor.rgb, a + ab * t));
    }
    if (onRamp < 0.004 && fract((vStripePos.x * 0.6 + vStripePos.y) * uStripeFreq) < 0.5) {
      diffuseColor.rgb *= uStripeDark;
    }
  }
`;

export interface StripedOptions extends ToonOptions {
  /** The ramp the bands go on, dark to light (linear; four steps, as `angry` has). */
  ramp: readonly Color[];
}

/** A vertex-coloured toon material that bands the surfaces coloured from `ramp` (angry monsters). */
export function createStripedMaterial(opts: StripedOptions): MeshToonMaterial {
  const material = createToonMaterial(opts);
  const base = material.onBeforeCompile.bind(material);
  const first = opts.ramp[0];
  if (!first) throw new Error('striped material: empty ramp');
  const ramp = [0, 1, 2, 3].map((i) => (opts.ramp[Math.min(i, opts.ramp.length - 1)] ?? first).clone());
  material.onBeforeCompile = (shader, renderer) => {
    base(shader, renderer);
    shader.uniforms['uStripeRamp'] = { value: ramp };
    shader.uniforms['uStripeFreq'] = { value: STRIPE_FREQ };
    shader.uniforms['uStripeDark'] = { value: STRIPE_DARK };
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', `${STRIPE_VERT_DECL}void main() {`)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vStripePos = transformed;');
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${STRIPE_FRAG_DECL}void main() {`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${STRIPE_APPLY}`);
  };
  const bias = opts.depthBias ?? 0;
  material.customProgramCacheKey = () => (bias === 0 ? 'toon-mrt-stripes' : 'toon-mrt-bias-stripes');
  return material;
}
